import { debounce } from "lodash-es";
import { useMemo, useState } from "react";
import { QueryKey } from "react-query";

import { IHttpRequestConfig } from "@/browser";
import { IPageable } from "@/core/api";
import { useInfiniteQueryFlatData } from "@/core/queries";
import { InfiniteFetchOptions, useInfiniteQuery } from "@/core/queries/useInfiniteQuery";

export type PageableFetchFunction<Option, Params> = (
  params: Params,
  options?: IHttpRequestConfig,
) => Promise<IPageable<Option[]>>;

export interface IUseInfiniteOptionsParams<Option, Params> {
  queryKey: QueryKey;
  fetchData: PageableFetchFunction<Option, Params>;
  params: Params;
  options?: InfiniteFetchOptions<Option, unknown>;
}

const debounceTime = 500;
const minQueryStringLength = 1;

export function useInfiniteOptions<Option, Params>({
  queryKey,
  fetchData,
  params,
  options,
}: IUseInfiniteOptionsParams<Option, Params>) {
  const [query, setQuery] = useState("");

  const { isLoading, isFetching, data, fetchNextPage } = useInfiniteQuery(
    queryKey,
    fetchData,
    {
      ...params,
      query,
    },
    options,
  );

  const debouncedSetQuery = useMemo(() => debounce(setQuery, debounceTime), []);

  const { flatData } = useInfiniteQueryFlatData(undefined, data);

  const handleQueryChange = async (queryString: string) => {
    if (queryString.length >= minQueryStringLength) {
      debouncedSetQuery(queryString);

      return;
    }

    debouncedSetQuery("");
    debouncedSetQuery.flush();
  };

  return { fetchNextPage, handleQueryChange, selectOptions: flatData, working: isLoading || isFetching };
}
